import {
  Customer,
  CustomerOutstanding,
  CustomerSummary,
  TermsAndConditions,
  WhitelabelAccount,
} from './actions';
import { api } from 'api';
import { requestErrorAction } from 'common/utils/requestErrorHandling';
import { confirmInDangerousDev } from 'common/utils/dev';

// #region CustomerSummary
export interface CustomerSummariesParams {
  customer_id?: number;
  include_outstanding_balances?: boolean;
}

export const doGetAllCustomerSummaries = () => async (dispatch) => {
  // We send two requests for the customer summaries.
  // The outstanding balances calculation is incredibly expensive, and we don't
  // want to have to wait for it to complete before we show the rest of the data,
  // so the first request skips that calculation.
  doGetCustomerSummaries({include_outstanding_balances: false})(dispatch)
  // The second request includes the outstanding balance data, and replaces the previous data in the state.
  doGetCustomerSummaries({include_outstanding_balances: true})(dispatch)
}

export const requestCustomerSummaries = async (
  params?: CustomerSummariesParams
) => await api.get<CustomerSummary[]>('/summary/', { params });

export const doGetCustomerSummaries =
  (params?: CustomerSummariesParams) => async (dispatch) => {
    // Outstanding balance requests are in the background, so we don't need to display any loading icons.
    if (!params.include_outstanding_balances) {
      dispatch({ type: 'customers/summaries/started'  });
    }

    try {
      const response = await requestCustomerSummaries(params);

      dispatch({
        type: 'customers/summaries/finished',
        summaries: response.data,
      });

      // If we didn't get outstanding balances when we were expecting them, we need to try again.
      if (params.include_outstanding_balances && response.data.filter(s => !s.balances.outstanding).length > 0) {
        setTimeout(() => doGetCustomerSummaries({include_outstanding_balances: true})(dispatch), 60000)
      }
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'customers/summaries/finished' },
          e,
          `Failed to get customer summaries.`
        )
      );
    }
  };
// #endregion

// #region Customer
export const requestCustomer = async (username: string) =>
  await api.get<Customer>(`/contact/${username}/`);


export const doGetCustomer = (username: string) => async (dispatch) => {
  dispatch({ type: 'customer/started' });

  try {
    const response = await requestCustomer(username);

    dispatch({
      type: 'customer/finished',
      customer: response.data,
    });
  } catch (e) {
    dispatch(
      requestErrorAction(
        { type: 'customer/finished' },
        e,
        `Failed to get customer "${username}".`
      )
    );
  }
};

export interface CustomerOustandingsParams {
  group_system?: number;
  group_account?: number;
  group_stream?: number;
}

export const requestCustomerOutstandings = async (
  params?: CustomerOustandingsParams
) =>
  await api.get<CustomerOutstanding[]>(`/weekops/customer/outstanding/`, {
    params,
  });

export const doGetCustomerOutstandings =
  (params?: CustomerOustandingsParams) => async (dispatch) => {
    dispatch({ type: 'customers/outstandings/started' });

    try {
      const response = await requestCustomerOutstandings(params);

      dispatch({
        type: 'customers/outstandings/finished',
        outstandings: response.data,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'customers/outstandings/finished' },
          e,
          `Failed to get customer oustandings.`
        )
      );
    }
  };

export interface SettleCustomerBodyData {
  amount: string;
  currency: string;
  date: string;
  description: string;
  post_date: string;
}

export const requestSettleCustomer = async (
  username: string,
  data: SettleCustomerBodyData
) =>
  confirmInDangerousDev(
    async () => await api.post(`/weekops/customer/${username}/clear/`, data)
  );

export const doSettleCustomer =
  (username: string, data: SettleCustomerBodyData) => async (dispatch) => {
    dispatch({ type: 'customers/settle/started' });

    try {
      await requestSettleCustomer(username, data);

      dispatch({
        type: 'customers/settle/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'customers/settle/finished' },
          e,
          `Failed to settle customer.`
        )
      );
    }
  };

export interface ReconcileCustomerBodyData {
  amount: string;
  currency: string;
  date: string;
  system?: string;
  stream?: string;
  account?: string;
  post_date: string;
}

export const requestReconcileCustomer = async (
  username: string,
  data: ReconcileCustomerBodyData
) =>
  confirmInDangerousDev(
    async () => await api.post(`/weekops/customer/${username}/reconcile/`, data)
  );

export const doReconcileCustomer =
  (username: string, data: ReconcileCustomerBodyData) => async (dispatch) => {
    dispatch({ type: 'customers/reconcile/started' });

    try {
      await requestReconcileCustomer(username, data);

      dispatch({
        type: 'customers/reconcile/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'customers/reconcile/finished' },
          e,
          `Failed to reconcile customer.`
        )
      );
    }
  };

export interface CurrencyConvertBodyData {
  from_amount: string;
  from_currency: string;
  to_amount: string;
  to_currency: string;
  stream: string;
  system: string;
  account: string;
  post_date: string;
}

export const requestCurrencyConvert = async (
  username: string,
  data: CurrencyConvertBodyData
) =>
  confirmInDangerousDev(
    async () =>
      await api.post(`/weekops/customer/${username}/currency_convert/`, data)
  );

export const doCurrencyConvert =
  (username: string, data: CurrencyConvertBodyData) => async (dispatch) => {
    dispatch({ type: 'customers/currency_convert/started' });

    try {
      await requestCurrencyConvert(username, data);

      dispatch({
        type: 'customers/currency_convert/finished',
        success: true,
      });
    } catch (e) {
      dispatch(
        requestErrorAction(
          { type: 'customers/currency_convert/finished' },
          e,
          `Failed to convert currency.`
        )
      );
    }
  };
// #endregion

// #region WhitelabelAccount
export const requestWhitelabelAccounts = async () =>
  await api.get<WhitelabelAccount[]>('/weekops/account/client/whitelabel/');

export const doGetWhitelabelAccounts = () => async (dispatch) => {
  dispatch({ type: 'whitelabels/accounts/started' });

  try {
    const response = await requestWhitelabelAccounts();

    dispatch({
      type: 'whitelabels/accounts/finished',
      accounts: response.data,
    });
  } catch (e) {
    dispatch(
      requestErrorAction(
        { type: 'whitelabels/accounts/finished' },
        e,
        `Failed to get whitelabel accounts.`
      )
    );
  }
};
// #endregion

// #region TermsAndConditions
export const requestTermsAndConditions = async () =>
  await api.get<TermsAndConditions>('/contact/self/terms/');

export const doGetTermsAndConditions = () => async (dispatch) => {
  dispatch({ type: 'terms/started' });

  try {
    const response = await requestTermsAndConditions();

    dispatch({
      type: 'terms/finished',
      tncs: response.data,
    });
  } catch (e) {
    dispatch(
      requestErrorAction(
        { type: 'terms/finished' },
        e,
        `Failed to get terms and conditions.`
      )
    );
  }
};

export const requestAcceptTermsAndConditions = async () =>
  await api.post('/contact/self/terms/');

export const doAcceptTermsAndConditions = () => async (dispatch) => {
  dispatch({ type: 'terms/accept/started' });

  try {
    await requestAcceptTermsAndConditions();

    dispatch({
      type: 'terms/accept/finished',
    });
  } catch (e) {
    dispatch(
      requestErrorAction(
        { type: 'terms/accept/finished' },
        e,
        `Failed to accept terms and conditions.`
      )
    );
  }
};
// #endregion
